<template>
  <lpopup class="vcv-filter" width="300px" @hide="$emit('cancel')">
    <!-- TYPE - ASYNC LIST -->
    <template v-if="type === 'list'">
      <vinput
        v-if="showAsyncSearch"
        :value="keyword"
        border
        icon="search"
        :placeholder="$tr(placeholderSearch)"
        @input="onSearch"
      />

      <vinput v-if="showSearch" v-model="searchWord" border icon="search" :placeholder="$tr(placeholderSearch)" />

      <div v-if="!isLoading && list.length > 0" ref="list" v-scroll-end="loadMore" class="vcv-filter__list">
        <div class="vcv-filter__list-item">
          <vcheckbox :value="isSelectedAll" @input="toggleSelectAll" />

          <div @click.prevent.stop="toggleSelectAll">
            {{ $tr(keyword.length === 0 ? 'filter.list.select_all' : 'filter.list.select_searched') }}
          </div>
        </div>

        <div v-for="item in filteredList" :key="item.id" class="vcv-filter__list-item">
          <vcheckbox :value="selectedList.includes(item.id)" @input="toggleSelect(item.id)" />

          <span @click.prevent.stop="toggleSelect(item.id)">
            {{ item.name }}
          </span>
        </div>
      </div>

      <div v-else-if="!isLoading && filteredList.length === 0" class="vcv-filter__list">
        <div class="vcv-filter__list-nodata">{{ $tr('common.table-no-data') }}</div>
      </div>

      <div v-else class="vcv-filter__list">
        <vpreloader size="40" />
      </div>

      <div class="vcv-filter__controls">
        <div>
          <vbutton size="small" label="common.done" :disabled="isLoading" @click="$emit('apply', selectedList)" />

          <vbutton size="small" label="actions.cancel" :disabled="isLoading" color="grey" @click="$emit('cancel')" />
        </div>

        <div v-tooltip.top="$tr('filter.clear')" class="vcv-filter__clear" @click="clearFilter">
          <i class="icon icon-filter_clear"></i>
        </div>

        <vpreloader v-if="isLoadingList" class="vcv-filter__list-loading" size="20" width="4" />
      </div>
    </template>
  </lpopup>
</template>

<script>
import debounce from 'lodash/debounce';
import scrollEnd from '@/directives/scrollEnd';

export default {
  name: 'VcvTableFilter',

  directives: {
    scrollEnd
  },

  props: {
    type: {
      type: String,
      default: 'text'
    },

    showSearch: {
      type: Boolean,
      default: false
    },

    showAsyncSearch: {
      type: Boolean,
      default: false
    },

    placeholderSearch: {
      type: String,
      default: 'admin_console.companies.table.search'
    },

    list: {
      type: [Array, Object],
      default: () => []
    },

    keyword: {
      type: String,
      default: ''
    },

    isLoading: {
      type: Boolean,
      default: false
    },

    isLoadingList: {
      type: Boolean,
      default: false
    },

    preSelected: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      searchWord: '',
      selectedList: [],
      scrolledToBottom: false
    };
  },

  computed: {
    isSelectedAll() {
      const arrayOfIdFromList = this.list.map(x => x.id);

      return (
        arrayOfIdFromList.length === this.selectedList.length &&
        arrayOfIdFromList.sort().every((value, index) => {
          return value === this.selectedList.sort()[index];
        })
      );
    },

    filteredList() {
      if (this.searchWord.length > 0) {
        return this.list.filter(x => x.name.toLowerCase().includes(this.searchWord.toLowerCase()));
      }

      return this.list;
    }
  },

  methods: {
    loadMore() {
      this.$emit('async-load-more');
    },

    toggleSelectAll() {
      if (this.isSelectedAll) {
        this.selectedList = [];
      } else {
        for (let i = 0; i < this.list.length; i++) {
          if (!this.selectedList.includes(this.list[i].id)) {
            this.selectedList.push(this.list[i].id);
          }
        }
      }
    },

    toggleSelect(itemId) {
      const index = this.selectedList.indexOf(itemId);

      if (index < 0) {
        this.selectedList.push(itemId);
      } else {
        this.selectedList.splice(index, 1);
      }
    },

    onSearch: debounce(function (keyword) {
      this.$emit('async-search', keyword);
    }, 500),

    clearFilter() {
      this.selectedList = [];
      this.$emit('clear');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.vcv-filter {
  position: absolute;
  top: 60px;
  left: 0;
  padding: 20px;
  border: none;
  box-shadow: $box-shadow;
  z-index: 1000;

  &__list {
    margin: 20px 0;
    position: relative;
    padding-left: 2px;
  }

  &__list-item {
    display: flex;
    margin-bottom: 10px;
    @include mixin-text-main;

    .vcheckbox {
      margin-right: 10px;
    }

    span {
      line-height: 1.4;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__list-nodata {
    @include mixin-text-main;
    color: $grey;
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
  }

  &__list-loading {
    position: absolute;
    background: rgba(255, 255, 255, 0.75);
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__clear {
    font-size: 18px;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    cursor: pointer;

    i {
      margin: 0;
      transition: color 0.3s;
    }

    &:hover {
      i {
        color: $green-hover;
        transition: color 0.3s;
      }
    }
  }
}
</style>
